import React, { useState, useEffect } from 'react';
import './ImageThumbnail.css';

const ImageThumbnail = ({ filePath, metadata, isSelected, onToggleSelection, onImageClick, onShowInFolder, onDeleteFile, disabled, index, version }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use custom protocol for Electron to serve local images
        // This avoids CORS and security issues with file:// protocol
        // (?v= cache-busts after in-place edits like marquee crop)
        const url = window.electronAPI
          ? `local-image://${encodeURIComponent(filePath)}${version ? `?v=${version}` : ''}`
          : `file://${filePath}`;

        console.log('ImageThumbnail debug:', {
          filePath,
          hasElectronAPI: !!window.electronAPI,
          url,
          encodedPath: encodeURIComponent(filePath)
        });

        setImageUrl(url);

        // Preload the image to check if it loads successfully
        const img = new Image();
        img.onload = () => {
          console.log('Image loaded successfully:', url);
          setIsLoading(false);
        };
        img.onerror = (err) => {
          console.error('Image failed to load:', {
            url,
            error: err,
            filePath
          });
          setError('Failed to load image');
          setIsLoading(false);
        };
        img.src = url;
        
      } catch (err) {
        setError('Error loading image');
        setIsLoading(false);
      }
    };

    if (filePath) {
      loadImage();
    }
  }, [filePath, version]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleCheckboxChange = (e) => {
    onToggleSelection(e.target.checked);
  };

  const handleThumbnailClick = (e) => {
    // Don't trigger if clicking on the checkbox
    if (e.target.type === 'checkbox') return;
    // Open lightbox instead of toggling selection
    if (onImageClick) {
      onImageClick();
    }
  };

  return (
    <div 
      className={`image-thumbnail ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={handleThumbnailClick}
      style={{ '--index': index }}
    >
      <div className="thumbnail-header">
        <label className="glass-checkbox" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            disabled={disabled}
          />
        </label>

        <div className="thumb-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="thumb-action-btn"
            title="Show in folder"
            disabled={disabled}
            onClick={() => onShowInFolder && onShowInFolder(filePath)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
            </svg>
          </button>
          <button
            className="thumb-action-btn thumb-action-trash"
            title="Move to Recycle Bin"
            disabled={disabled}
            onClick={() => onDeleteFile && onDeleteFile(filePath)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        </div>
      </div>

      <div className="thumbnail-image-container">
        {isLoading ? (
          <div className="thumbnail-loading">
            <div className="loading-spinner"></div>
            <span>Loading...</span>
          </div>
        ) : error ? (
          <div className="thumbnail-error">
            <div className="error-icon">⚠️</div>
            <span>{error}</span>
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={metadata?.fileName || 'Image'}
            className="thumbnail-image"
            draggable={false}
          />
        )}
      </div>

      <div className="thumbnail-info">
        <div className="thumbnail-filename">
          {metadata?.fileName || 'Unknown'}
        </div>
        
        <div className="thumbnail-details">
          {metadata && (
            <>
              <span className="detail-item">
                {metadata.width} × {metadata.height}
              </span>
              <span className="detail-item">
                {formatFileSize(metadata.fileSize)}
              </span>
              <span className="detail-item format-badge">
                {(() => {
                  // Fix AVIF being detected as HEIF
                  const ext = metadata.fileName?.toLowerCase().split('.').pop();
                  if (ext === 'avif' && metadata.format?.toLowerCase() === 'heif') {
                    return 'AVIF';
                  }
                  return metadata.format?.toUpperCase() || 'Unknown';
                })()}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Removed selection overlay - selection is now shown via grayscale/color difference */}
    </div>
  );
};

export default ImageThumbnail;